import GTM from '../../../gtm/GTM';
import GTMOverviewFilterGroupToggleModel from '../../../gtm/models/GTMOverviewFilterGroupToggleModel';
import GTMViewItemListModel from '../../../gtm/models/GTMViewItemListModel';
import type { GA4SearchTermType, GTMItemData } from '../../../gtm/GTM.types';
import { convertPriceStringToNumber } from '../../../util/dataLayer';
import { calculateNumberOfNights, hasInstantBooking } from '../../../gtm/GTMHelpers';
import HouseSearchFilters from '../store/HouseSearchFilters';
import HouseSearchListItems from '../store/HouseSearchListItems';
import KeywordFilter from '../store/KeywordFilter';
import DestinationFilter from '../../../common/search/store/DestinationFilter';
import PopularPageFilter from '../store/PopularPageFilter';

export class SearchFormComponentTracking {
    readonly #filters = HouseSearchFilters.getInstance();

    readonly #items = HouseSearchListItems.getInstance();

    readonly #destinationFilter: DestinationFilter;

    readonly #keywordFilter: KeywordFilter;

    readonly #popularPageFilter: PopularPageFilter;

    public constructor() {
        this.#destinationFilter = this.#filters.get(DestinationFilter.NAME) as DestinationFilter;

        this.#keywordFilter = this.#filters.get(KeywordFilter.NAME) as KeywordFilter;

        this.#popularPageFilter = this.#filters.get(PopularPageFilter.NAME) as PopularPageFilter;
    }

    public track(): void {
        const { allParams } = this.#filters;
        const { keyword } = this.#keywordFilter.parameters || {};

        GTM.trackEcommerce(
            GTMViewItemListModel.parseToGTMData({
                listId: 'search_results',
                listName: 'Search Results',
                items: this.#parseItems(),
                searchTerm: keyword ?? '',
                searchType: this.#getSearchTermType(),
                itemCount: this.#items.total,
                calendarType: allParams.arrivalDate ? 'fixed' : 'flexible',
                flexibleDuration: allParams.flexibleDurationType,
                flexibleMonths: allParams.flexibleBookingMonths,
                flexibleArrivalDate: allParams.flexibleArrivalDate,
                fixedDateArrival: allParams.arrivalDate,
                fixedDateDeparture: allParams.departureDate,
                adults: allParams.adults,
                children: allParams.children,
                babies: allParams.babies,
                pets: allParams.pets
            })
        );
    }

    public trackToggleFilterGroup(category?: string): void {
        GTM.track(
            GTMOverviewFilterGroupToggleModel.parseToGTMData({
                category: category || 'unknown'
            })
        );
    }

    #parseItems(): GTMItemData[] {
        return this.#items.houses.map((item): GTMItemData => {
            const priceString = item.price?.dayPrice;
            const price = convertPriceStringToNumber(priceString) ?? undefined;

            const quantity = calculateNumberOfNights({
                arrivalDate: item.price?.startDate ?? '',
                departureDate: item.price?.endDate ?? ''
            });

            return {
                id: item.id.toString(),
                title: item.title,
                country: item.country,
                region: item.region ?? '',
                department: item.department ?? '',
                place: item.place ?? '',
                discount: item.price?.discountPerNight || 0,
                pricePerNight: price || 0,
                instantBooking: hasInstantBooking(item.features),
                quantity: quantity || 1
            };
        });
    }

    #getSearchTermType(): GA4SearchTermType {
        const filter = Object.entries(this.#destinationFilter.parameters).find(
            ([, value]) => value !== null
        );

        if (filter) {
            const [key] = filter;
            return key as GA4SearchTermType;
        }

        const { naturalArea } = this.#popularPageFilter.parameters;

        if (naturalArea) {
            return 'nature_area';
        }

        return 'keyword';
    }
}

export default new SearchFormComponentTracking();
